"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { BsPin, BsPinFill } from "react-icons/bs";
import { FiEdit } from "react-icons/fi";
import { renderTextWithLinks } from "@/app/[tenant]/(console)/comms/chat/window/message/shared";
import { Divider } from "@/components/ui";
import { formatDate, formatStaffName } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import type { PatientChart } from "@/types/patient";
import { PatientSidebarSectionSkeleton } from "./section-skeleton";
import { useTogglePinQuickNoteMutation } from "@/api/provider/user/patient/quick-note-mutation";
import { quickNoteQueryOptions } from "@/api/provider/user/patient/quick-note-query";
import { useMemoWindowStore } from "@/stores/memo-window-store";
interface Props {
  patient: PatientChart;
}
export default function MemosSection(t0) {
  const $ = _c(8);
  const {
    patient
  } = t0;
  const patientId = patient.id;
  let t1;
  if ($[0] !== patientId) {
    t1 = quickNoteQueryOptions.list(patientId);
    $[0] = patientId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = !!patientId;
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = {
      ...t1,
      enabled: t2
    };
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    data: t4,
    isPending
  } = useQuery(t3);
  const quickNotes = t4 === undefined ? [] : t4;
  const openMemoWindow = useMemoWindowStore(_temp);
  const togglePinMutation = useTogglePinQuickNoteMutation();
  const filteredQuickNotes = quickNotes.filter(_temp2).slice().sort(_temp3);
  if (isPending) {
    let t5;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <PatientSidebarSectionSkeleton />;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    return t5;
  }
  const t5 = filteredQuickNotes.length > 0 ? <div className="max-h-60 overflow-y-auto pr-1">{filteredQuickNotes.map((note, index) => <div key={note.id || index}><div className={cx("p-2", note.isPinned && "rounded-md border border-l-4 border-border border-l-primary bg-background")}><div className="flex items-start justify-between gap-2"><p className="flex-1 whitespace-pre-wrap break-words text-body text-foreground">{renderTextWithLinks(note.note)}</p><div className="flex items-center gap-1"><button type="button" aria-label={note.isPinned ? "Unpin memo" : "Pin memo"} title={note.isPinned ? "Unpin memo" : "Pin memo"} className={cx("flex h-6 w-6 cursor-pointer items-center justify-center rounded hover:bg-muted", note.isPinned ? "text-primary" : "text-foreground-muted hover:text-foreground-secondary")} onClick={() => togglePinMutation.mutate({
              noteId: note.id,
              patientId
            })}>{note.isPinned ? <BsPinFill className="h-3.5 w-3.5" /> : <BsPin className="h-3.5 w-3.5" />}</button><div className="flex h-6 w-6 cursor-pointer items-center justify-center rounded text-foreground-muted hover:bg-muted hover:text-foreground-secondary" onClick={() => {
              openMemoWindow({
                patient,
                existingNote: note
              });
            }}><FiEdit className="h-3.5 w-3.5" /></div></div></div><div className="mt-1 flex items-center justify-between text-caption">{note.creator && <p className="text-caption text-foreground-secondary">By {formatStaffName(note.creator)}</p>}{note.createdDate && <p className="text-caption text-muted-foreground">{formatDate(note.createdDate)}</p>}</div></div>{index !== filteredQuickNotes.length - 1 && <Divider className="mx-2 my-0" />}</div>)}</div> : <p className="text-caption text-muted-foreground">No memos created</p>;
  let t6;
  if ($[6] !== t5) {
    t6 = <>{t5}</>;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  return t6;
}
function _temp3(a, b) {
  return Number(b.isPinned) - Number(a.isPinned);
}
function _temp2(n) {
  return !n.eventId;
}
function _temp(s) {
  return s.openMemoWindow;
}